import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Building2,
  Check,
  Loader2,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import { createCheckout, getCheckoutStatus } from "@/lib/api";
import { setUnlockToken } from "@/lib/aiUsage";
import { setUnlock as setMedUnlock } from "@/lib/medStorage";
import { toast } from "sonner";
import useSEO from "@/lib/useSEO";

const PriceCard = ({ id, title, price, period, blurb, features, cta, onClick, loading, highlight }) => (
  <div
    data-testid={`pricing-${id}`}
    className={`rounded-2xl p-7 border ${
      highlight ? "border-[#2B5339] bg-white shadow-md" : "border-[#E5E2DC] bg-white"
    } flex flex-col`}
  >
    <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold">{title}</div>
    <div className="mt-3 flex items-baseline gap-1">
      <span className="font-display text-5xl text-[#1C2B23] font-mono-num">{price}</span>
      <span className="text-[#5C6B62] text-sm">{period}</span>
    </div>
    <p className="text-[#5C6B62] mt-3 leading-relaxed text-sm">{blurb}</p>
    <ul className="mt-5 space-y-2 flex-1">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-[#1C2B23]">
          <Check size={14} className="text-[#2B5339] shrink-0 mt-0.5" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <button
      data-testid={`buy-${id}`}
      onClick={onClick}
      disabled={loading}
      type="button"
      className={`mt-6 rounded-full py-3 text-sm inline-flex items-center justify-center gap-2 ${
        highlight
          ? "bg-[#2B5339] text-white hover:bg-[#1E3B28]"
          : "bg-transparent border border-[#2B5339] text-[#2B5339] hover:bg-[#F0EFEA]"
      } disabled:opacity-60`}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : null}
      {cta}
    </button>
  </div>
);

export default function Pricing() {
  useSEO({
    title: "Pricing · The Calculator Is Free Forever · Centennial",
    description: "Centennial's retirement calculator is 100% free with no signup. Optional add-ons: $3 unlimited AI critic, $19/mo white-label embed for advisors, $99/yr featured advisor listing.",
    canonicalPath: "/pricing",
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [buying, setBuying] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  useEffect(() => {
    const sid = searchParams.get("session_id");
    const canceled = searchParams.get("canceled");
    if (canceled) {
      toast.message("Checkout canceled — feel free to try again.");
      return;
    }
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const s = await getCheckoutStatus(sid);
        if (s.payment_status === "paid") {
          if (s.unlock_token) {
            if (s.package_id === "med_safety_unlock_single" || s.package_id === "med_safety_unlock_family") {
              setMedUnlock(s.unlock_token);
            } else {
              setUnlockToken(s.unlock_token);
            }
            toast.success("Payment confirmed — unlock token saved to this browser.");
          }
          setPollResult(s);
          setPolling(false);
          return;
        }
        if (s.status === "expired") {
          toast.error("Checkout expired. Please try again.");
          setPolling(false);
          return;
        }
        if (attempts < 8) {
          setTimeout(tick, 2000);
        } else {
          toast.message("Still processing — refresh in a few seconds.");
          setPolling(false);
        }
      } catch (e) {
        console.error(e);
        setPolling(false);
      }
    };
    tick();
  }, [searchParams]);

  const buy = async (pkg) => {
    setBuying(pkg);
    try {
      const { url } = await createCheckout(pkg);
      window.location.href = url;
    } catch (e) {
      console.error(e);
      toast.error("Failed to open checkout. Try again.");
      setBuying(null);
    }
  };

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#F9F8F6] text-[#1C2B23]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm hover:text-[#2B5339]">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="font-display text-[#1C2B23] tracking-tight text-lg">
            Centennial<span className="text-[#C26747]">.</span>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-14 lg:py-20">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold">
          optional add-ons
        </div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl tracking-tight font-medium max-w-3xl">
          The calculator stays free. <span className="italic text-[#2B5339]">Forever.</span>
        </h1>
        <p className="mt-4 text-[#5C6B62] max-w-2xl">
          These optional purchases cover our AI and infrastructure costs and let financial advisors
          embed Centennial under their own brand. Individuals never need to pay.
        </p>

        {polling && (
          <div data-testid="pricing-polling" className="mt-8 inline-flex items-center gap-2 bg-white border border-[#E5E2DC] rounded-full px-4 py-2 text-sm text-[#5C6B62]">
            <Loader2 size={14} className="animate-spin" /> Confirming your payment…
          </div>
        )}

        {pollResult && (
          <div data-testid="pricing-success" className="mt-8 bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-5 text-[#1C2B23] max-w-xl">
            <div className="font-display text-lg flex items-center gap-2">
              <ShieldCheck size={18} /> You&apos;re all set
            </div>
            <p className="text-sm mt-2">
              {pollResult.package_id === "ai_unlock_30d"
                ? "Unlimited AI critiques active for the next 30 days."
                : pollResult.package_id === "sponsor_homepage_monthly"
                ? "Sponsor slot active for 30 days — upload your logo on the sponsor dashboard."
                : (pollResult.package_id === "med_safety_unlock_single" || pollResult.package_id === "med_safety_unlock_family")
                ? "Medication Safety Card unlocked — unlimited medications on this device."
                : "Advisor white-label embed unlocked — configure your branding in the advisor dashboard."}
            </p>
            <button
              type="button"
              onClick={() => navigate(
                pollResult.package_id === "advisor_monthly" ? "/advisor"
                : pollResult.package_id === "advisor_listing_yearly" ? "/advisor#featured"
                : pollResult.package_id === "sponsor_homepage_monthly" ? "/sponsor"
                : (pollResult.package_id === "med_safety_unlock_single" || pollResult.package_id === "med_safety_unlock_family") ? "/medication-safety"
                : "/"
              )}
              className="mt-4 bg-[#2B5339] text-white rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
            >
              {pollResult.package_id === "advisor_monthly" ? "Open advisor dashboard"
                : pollResult.package_id === "advisor_listing_yearly" ? "Set up your listing"
                : pollResult.package_id === "sponsor_homepage_monthly" ? "Set up your sponsor entry"
                : (pollResult.package_id === "med_safety_unlock_single" || pollResult.package_id === "med_safety_unlock_family") ? "Open medication safety card"
                : "Continue to calculator"}
            </button>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7 max-w-6xl">
          <PriceCard
            id="ai_unlock_30d"
            title="AI Plan Critic · Unlimited"
            price="$3"
            period="/ 30 days"
            blurb="Free tier covers 3 critiques per day. This one-time unlock removes the limit for a month — covers our LLM compute cost."
            features={[
              "Unlimited AI plan critiques",
              "Same Claude Sonnet 4.5 quality",
              "Token stored in your browser",
              "No subscription · no auto-renew",
            ]}
            cta="Unlock for $3"
            highlight
            loading={buying === "ai_unlock_30d"}
            onClick={() => buy("ai_unlock_30d")}
          />
          <PriceCard
            id="advisor_monthly"
            title="Advisor White-Label Embed"
            price="$19"
            period="/ month"
            blurb="For financial advisors and coaches who want to embed Centennial under their own brand on their site."
            features={[
              "Custom logo & brand color",
              "Remove 'powered by' badge",
              "Unlimited usage on your site",
              "Free for personal blog use without this",
            ]}
            cta="Subscribe — $19/mo"
            loading={buying === "advisor_monthly"}
            onClick={() => buy("advisor_monthly")}
          />
          <PriceCard
            id="advisor_listing_yearly"
            title="Featured Advisor Listing"
            price="$99"
            period="/ year"
            blurb="A public listing on our /featured page. Drives qualified prospects from people who just modeled their plan."
            features={[
              "Profile on /featured for 12 months",
              "Photo, bio, location, website",
              "Editable anytime via your token",
              "No vetting — your reputation, your domain",
            ]}
            cta="Get listed — $99/yr"
            loading={buying === "advisor_listing_yearly"}
            onClick={() => buy("advisor_listing_yearly")}
          />
          <PriceCard
            id="sponsor_homepage_monthly"
            title="Homepage Sponsor Slot"
            price="$199"
            period="/ month"
            blurb="One of 3 rotating logo slots on the Centennial homepage 'Trusted by' strip — seen by every visitor."
            features={[
              "Logo + link on the homepage",
              "Up to 3 sponsors at a time",
              "Click-through tracking",
              "Fee-only advisors & fintech only",
            ]}
            cta="Sponsor — $199/mo"
            loading={buying === "sponsor_homepage_monthly"}
            onClick={() => buy("sponsor_homepage_monthly")}
          />
        </div>

        <div className="mt-12 text-[11px] text-[#5C6B62] flex items-start gap-2 max-w-3xl">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          <span>
            Test mode is active — use Stripe's test card 4242 4242 4242 4242 with any future expiry,
            any CVC, and any ZIP. No real charge will occur.
          </span>
        </div>
      </section>
    </div>
  );
}
